import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../context/ProfileContext';
import { useTutorial } from '../context/TutorialContext';
import { useTheme } from '../context/ThemeContext';
import { useAudioSettings } from '../context/AudioSettingsContext';
import { supabase } from '../lib/supabase';
import { locationService } from '../lib/locationService';

// Icons
const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const MapPinIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const LockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const MoonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

const SunIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" />
    <line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" />
    <line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);

const Preferences = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { userProfile, activeProfile, refreshProfile } = useProfile();
  const { isDark, toggleTheme, theme } = useTheme();
  const { hideMyPosts, setHideMyPosts } = useTutorial();
  const { settings: audioSettings, updateSetting: updateAudioSetting } = useAudioSettings();

  // Location state
  const [locationEnabled, setLocationEnabled] = useState(false);
  const [locationMethod, setLocationMethod] = useState('gps');
  const [manualLocation, setManualLocation] = useState('');
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [showLocationEdit, setShowLocationEdit] = useState(false);

  // Appearance state
  const [skipTitleScreen, setSkipTitleScreen] = useState(false);
  const [preferDigital, setPreferDigital] = useState(false);

  // Privacy state
  const [hideProfile, setHideProfile] = useState(false);

  // Pricing state
  const [pricingEnabled, setPricingEnabled] = useState(false);

  // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      // Load skip title preference
      const skipTitle = localStorage.getItem('skipTitleScreen');
      setSkipTitleScreen(skipTitle === 'true');

      // Load image preference
      const imgPref = localStorage.getItem('imagePreference');
      setPreferDigital(imgPref !== 'photo'); // default to digital

      // Load location
      if (userProfile?.id) {
        const { data: locationData } = await supabase
          .from('user_locations')
          .select('*')
          .eq('user_id', userProfile.id)
          .single();

        if (locationData) {
          setCurrentLocation({
            city: locationData.city,
            state: locationData.state,
            lat: locationData.latitude,
            lng: locationData.longitude,
          });
          setLocationMethod(locationData.location_method || 'gps');
          setLocationEnabled(true);
        }

        // Load privacy setting
        const { data: privacyData } = await supabase
          .from('users')
          .select('hide_from_search')
          .eq('id', userProfile.id)
          .single();

        if (privacyData) {
          setHideProfile(privacyData.hide_from_search || false);
        }
      }
    };

    loadSettings();
  }, [userProfile?.id, activeProfile?.id]);

  // Sync pricing state from profile context (handles refresh after toggle + navigation)
  useEffect(() => {
    console.log('[Preferences] pricing sync effect fired:', {
      'activeProfile?.pricing_enabled': activeProfile?.pricing_enabled,
      'activeProfile?.id': activeProfile?.id,
      'will set pricingEnabled to': activeProfile?.pricing_enabled === true,
    });
    setPricingEnabled(activeProfile?.pricing_enabled === true);
  }, [activeProfile?.pricing_enabled]);

  // Location handlers
  const handleGetGPSLocation = async () => {
    setLocationLoading(true);
    setLocationError(null);

    try {
      const location = await locationService.getCurrentLocation();
      if (location) {
        setCurrentLocation(location);
        setLocationEnabled(true);

        // Save to database
        if (userProfile?.id) {
          await supabase.from('user_locations').upsert({
            user_id: userProfile.id,
            city: location.city,
            state: location.state,
            latitude: location.lat,
            longitude: location.lng,
            location_method: 'gps',
            updated_at: new Date().toISOString(),
          }, { onConflict: 'user_id' });
        }
      }
    } catch (err) {
      setLocationError(err.message || 'Failed to get location');
    } finally {
      setLocationLoading(false);
    }
  };

  const handleManualLocationSearch = async () => {
    if (!manualLocation.trim()) return;

    setLocationLoading(true);
    setLocationError(null);

    try {
      const location = await locationService.searchLocation(manualLocation);
      if (location) {
        setCurrentLocation(location);
        setLocationEnabled(true);
        setManualLocation('');

        // Save to database
        if (userProfile?.id) {
          await supabase.from('user_locations').upsert({
            user_id: userProfile.id,
            city: location.city,
            state: location.state,
            latitude: location.lat,
            longitude: location.lng,
            location_method: 'manual',
            updated_at: new Date().toISOString(),
          }, { onConflict: 'user_id' });
        }
      }
    } catch (err) {
      setLocationError(err.message || 'Location not found');
    } finally {
      setLocationLoading(false);
    }
  };

  const handleDisableLocation = async () => {
    setCurrentLocation(null);
    setLocationEnabled(false);

    if (userProfile?.id) {
      await supabase
        .from('user_locations')
        .delete()
        .eq('user_id', userProfile.id);
    }
  };

  // Appearance handlers
  const handleToggleSkipTitle = async () => {
    const newValue = !skipTitleScreen;
    setSkipTitleScreen(newValue);
    localStorage.setItem('skipTitleScreen', newValue.toString());
    if (userProfile?.id) {
      supabase.from('users').update({ pref_skip_title_screen: newValue }).eq('id', userProfile.id).then(() => {});
    }
  };

  const handleToggleMusic = () => {
    console.log('[Preferences] handleToggleMusic:', {
      oldVal: audioSettings.musicEnabled,
      newVal: !audioSettings.musicEnabled,
    });
    updateAudioSetting('musicEnabled', !audioSettings.musicEnabled);
  };

  const handleToggleSound = () => {
    console.log('[Preferences] handleToggleSound:', {
      oldVal: audioSettings.soundEnabled,
      newVal: !audioSettings.soundEnabled,
    });
    updateAudioSetting('soundEnabled', !audioSettings.soundEnabled);
  };

  const handleToggleImagePreference = () => {
    const newValue = !preferDigital;
    setPreferDigital(newValue);
    localStorage.setItem('imagePreference', newValue ? 'digital' : 'photo');
  };

  // Pricing handler
  const handleTogglePricing = async () => {
    const newVal = !pricingEnabled;
    console.log('[Preferences] handleTogglePricing called:', {
      oldVal: pricingEnabled,
      newVal,
      'activeProfile?.id': activeProfile?.id,
      'userProfile?.id': userProfile?.id,
    });
    setPricingEnabled(newVal);
    if (activeProfile?.id) {
      const { data, error } = await supabase
        .from('users')
        .update({ pricing_enabled: newVal })
        .eq('id', activeProfile.id)
        .select();
      console.log('[Preferences] pricing DB update result:', { data, error });
      console.log('[Preferences] calling refreshProfile...');
      await refreshProfile();
      console.log('[Preferences] refreshProfile done, activeProfile?.pricing_enabled:', activeProfile?.pricing_enabled);
    } else {
      console.log('[Preferences] NO activeProfile.id - skipped DB update!');
    }
  };

  const handleToggleHideProfile = async () => {
    const newValue = !hideProfile;
    setHideProfile(newValue);

    if (userProfile?.id) {
      await supabase
        .from('users')
        .update({ hide_from_search: newValue })
        .eq('id', userProfile.id);
    }
  };

  // Toggle component
  const Toggle = ({ value, onChange }) => (
    <button
      style={{
        ...styles.toggle,
        backgroundColor: value ? theme.accent : theme.border,
      }}
      onClick={onChange}
    >
      <div
        style={{
          ...styles.toggleKnob,
          transform: value ? 'translateX(20px)' : 'translateX(0)',
        }}
      />
    </button>
  );

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: theme.bg,
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      backgroundColor: theme.bgCard,
      padding: '12px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      borderBottom: `1px solid ${theme.border}`,
    },
    backButton: {
      background: 'none',
      border: 'none',
      padding: 8,
      cursor: 'pointer',
      color: theme.accent,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    headerTitle: {
      fontSize: 18,
      fontWeight: 600,
      color: theme.text,
      margin: 0,
    },
    content: {
      flex: 1,
      padding: 16,
      maxWidth: 600,
      margin: '0 auto',
      width: '100%',
    },
    sectionHeader: {
      fontSize: 11,
      fontWeight: 600,
      color: theme.textMuted,
      textTransform: 'uppercase',
      letterSpacing: 1,
      margin: '20px 0 10px 0',
    },
    section: {
      backgroundColor: theme.bgCard,
      borderRadius: 12,
      padding: 16,
      marginBottom: 12,
      border: `1px solid ${theme.border}`,
    },
    // Location styles
    locationHeaderRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 8,
    },
    locationHeaderLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      color: theme.accent,
    },
    locationTitle: {
      fontSize: 14,
      fontWeight: 600,
      color: theme.text,
      margin: 0,
    },
    changeButton: {
      background: 'none',
      border: 'none',
      color: theme.accent,
      fontSize: 13,
      fontWeight: 500,
      cursor: 'pointer',
    },
    locationDisplay: {
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
    },
    locationCity: {
      fontSize: 15,
      fontWeight: 500,
      color: theme.text,
    },
    locationMethod: {
      fontSize: 12,
      color: theme.textMuted,
    },
    noLocation: {
      fontSize: 14,
      color: theme.textMuted,
    },
    locationEditSection: {
      marginTop: 16,
      paddingTop: 16,
      borderTop: `1px solid ${theme.border}`,
    },
    privacyNotice: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 8,
      backgroundColor: isDark ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
      padding: 12,
      borderRadius: 8,
      marginBottom: 12,
      color: theme.textSecondary,
    },
    privacyText: {
      fontSize: 12,
      margin: 0,
      lineHeight: 1.4,
    },
    methodSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
    },
    optionCard: {
      padding: 12,
      borderRadius: 8,
      border: `1px solid ${theme.border}`,
      cursor: 'pointer',
    },
    optionCardSelected: {
      borderColor: theme.accent,
      backgroundColor: isDark ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
    },
    optionHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      marginBottom: 4,
    },
    optionRadio: {
      width: 18,
      height: 18,
      borderRadius: '50%',
      border: `2px solid ${theme.border}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    optionRadioActive: {
      borderColor: theme.accent,
    },
    optionRadioDot: {
      width: 10,
      height: 10,
      borderRadius: '50%',
      backgroundColor: theme.accent,
    },
    optionTitle: {
      fontSize: 14,
      fontWeight: 500,
      color: theme.text,
    },
    optionDesc: {
      fontSize: 12,
      color: theme.textMuted,
      marginLeft: 26,
    },
    manualInputRow: {
      display: 'flex',
      gap: 8,
      marginTop: 12,
    },
    manualInput: {
      flex: 1,
      padding: '10px 12px',
      borderRadius: 8,
      border: `1px solid ${theme.border}`,
      backgroundColor: theme.bgCard,
      color: theme.text,
      fontSize: 14,
    },
    searchButton: {
      padding: '10px 16px',
      borderRadius: 8,
      border: 'none',
      backgroundColor: theme.accent,
      color: '#fff',
      fontSize: 14,
      fontWeight: 600,
      cursor: 'pointer',
    },
    gpsButton: {
      marginTop: 12,
      padding: '10px 16px',
      borderRadius: 8,
      border: 'none',
      backgroundColor: theme.accent,
      color: '#fff',
      fontSize: 14,
      fontWeight: 600,
      cursor: 'pointer',
      width: '100%',
    },
    disableButton: {
      marginTop: 8,
      padding: '10px 16px',
      borderRadius: 8,
      border: `1px solid ${theme.error}`,
      backgroundColor: 'transparent',
      color: theme.error,
      fontSize: 13,
      fontWeight: 500,
      cursor: 'pointer',
      width: '100%',
    },
    loadingBox: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: 16,
      backgroundColor: theme.bgSheet,
      borderRadius: 8,
    },
    smallSpinner: {
      width: 20,
      height: 20,
      border: `2px solid ${theme.border}`,
      borderTop: `2px solid ${theme.accent}`,
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
    },
    loadingText: {
      fontSize: 13,
      color: theme.textSecondary,
    },
    errorBox: {
      marginTop: 12,
      padding: 12,
      backgroundColor: isDark ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)',
      borderRadius: 8,
      border: `1px solid ${theme.error}`,
    },
    errorText: {
      fontSize: 13,
      color: theme.error,
    },
    // Setting row styles
    settingRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 0',
      borderBottom: `1px solid ${theme.border}`,
    },
    settingRowLast: {
      borderBottom: 'none',
    },
    settingInfo: {
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
    },
    settingLabel: {
      fontSize: 14,
      fontWeight: 500,
      color: theme.text,
    },
    settingDesc: {
      fontSize: 12,
      color: theme.textMuted,
    },
    toggle: {
      width: 48,
      height: 28,
      borderRadius: 14,
      border: 'none',
      cursor: 'pointer',
      position: 'relative',
      transition: 'background-color 0.2s',
      padding: 0,
      flexShrink: 0,
    },
    toggleKnob: {
      width: 24,
      height: 24,
      borderRadius: 12,
      backgroundColor: '#fff',
      position: 'absolute',
      top: 2,
      left: 2,
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      transition: 'transform 0.2s',
    },
    themeToggle: {
      width: 48,
      height: 28,
      borderRadius: 14,
      border: `1px solid ${theme.border}`,
      cursor: 'pointer',
      position: 'relative',
      transition: 'background-color 0.2s',
      padding: 0,
      flexShrink: 0,
    },
    themeToggleKnob: {
      width: 24,
      height: 24,
      borderRadius: 12,
      position: 'absolute',
      top: 1,
      left: 1,
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      transition: 'transform 0.2s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: theme.textSecondary,
    },
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(-1)}>
          <BackIcon />
        </button>
        <h1 style={styles.headerTitle}>Preferences</h1>
      </div>

      {/* Content */}
      <div style={styles.content}>
        {/* Location Section */}
        <h4 style={styles.sectionHeader}>Location</h4>
        <div style={styles.section}>
          <div style={styles.locationHeaderRow}>
            <div style={styles.locationHeaderLeft}>
              <MapPinIcon />
              <h3 style={styles.locationTitle}>Current Location</h3>
            </div>
            <button style={styles.changeButton} onClick={() => setShowLocationEdit(!showLocationEdit)}>
              {showLocationEdit ? 'Done' : 'Change'}
            </button>
          </div>

          {currentLocation ? (
            <div style={styles.locationDisplay}>
              <span style={styles.locationCity}>
                {currentLocation.city && currentLocation.state
                  ? `${currentLocation.city}, ${currentLocation.state}`
                  : 'Location Set'}
              </span>
              <span style={styles.locationMethod}>
                via {locationMethod === 'gps' ? 'GPS' : 'Manual Entry'}
              </span>
            </div>
          ) : (
            <div style={styles.locationDisplay}>
              <span style={styles.noLocation}>No location set</span>
            </div>
          )}

          {showLocationEdit && (
            <div style={styles.locationEditSection}>
              <div style={styles.privacyNotice}>
                <LockIcon />
                <p style={styles.privacyText}>
                  Your exact location is never shared. It's only used to find nearby traders and card shops.
                </p>
              </div>

              {locationLoading && (
                <div style={styles.loadingBox}>
                  <div style={styles.smallSpinner} />
                  <span style={styles.loadingText}>Getting your location...</span>
                </div>
              )}

              {!locationLoading && (
                <div style={styles.methodSection}>
                  <div
                    style={{
                      ...styles.optionCard,
                      ...(locationMethod === 'manual' ? styles.optionCardSelected : {}),
                    }}
                    onClick={() => setLocationMethod('manual')}
                  >
                    <div style={styles.optionHeader}>
                      <div style={{
                        ...styles.optionRadio,
                        ...(locationMethod === 'manual' ? styles.optionRadioActive : {}),
                      }}>
                        {locationMethod === 'manual' && <div style={styles.optionRadioDot} />}
                      </div>
                      <span style={styles.optionTitle}>Enter Manually</span>
                    </div>
                    <span style={styles.optionDesc}>Type your city or zip code</span>

                    {locationMethod === 'manual' && (
                      <div style={styles.manualInputRow} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="text"
                          placeholder="e.g. Costa Mesa or 92626"
                          value={manualLocation}
                          onChange={(e) => setManualLocation(e.target.value)}
                          style={styles.manualInput}
                          onKeyDown={(e) => e.key === 'Enter' && handleManualLocationSearch()}
                        />
                        <button
                          style={{
                            ...styles.searchButton,
                            opacity: !manualLocation.trim() ? 0.5 : 1,
                          }}
                          onClick={handleManualLocationSearch}
                          disabled={locationLoading || !manualLocation.trim()}
                        >
                          Set
                        </button>
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      ...styles.optionCard,
                      ...(locationMethod === 'gps' ? styles.optionCardSelected : {}),
                    }}
                    onClick={() => setLocationMethod('gps')}
                  >
                    <div style={styles.optionHeader}>
                      <div style={{
                        ...styles.optionRadio,
                        ...(locationMethod === 'gps' ? styles.optionRadioActive : {}),
                      }}>
                        {locationMethod === 'gps' && <div style={styles.optionRadioDot} />}
                      </div>
                      <span style={styles.optionTitle}>Use GPS</span>
                    </div>
                    <span style={styles.optionDesc}>Automatically detect your location</span>

                    {locationMethod === 'gps' && (
                      <button
                        style={styles.gpsButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleGetGPSLocation();
                        }}
                      >
                        Get My Location
                      </button>
                    )}
                  </div>

                  {currentLocation && (
                    <button style={styles.disableButton} onClick={handleDisableLocation}>
                      Disable Location
                    </button>
                  )}
                </div>
              )}

              {locationError && (
                <div style={styles.errorBox}>
                  <span style={styles.errorText}>{locationError}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Appearance Section */}
        <h4 style={styles.sectionHeader}>Appearance</h4>
        <div style={styles.section}>
          <div style={styles.settingRow}>
            <div style={styles.settingInfo}>
              <span style={styles.settingLabel}>Dark Mode</span>
              <span style={styles.settingDesc}>
                {isDark ? 'Using dark theme' : 'Using light theme'}
              </span>
            </div>
            <button
              style={{
                ...styles.themeToggle,
                backgroundColor: isDark ? theme.accent : theme.bgHover,
                borderColor: isDark ? theme.accent : theme.border,
              }}
              onClick={toggleTheme}
            >
              <div
                style={{
                  ...styles.themeToggleKnob,
                  transform: isDark ? 'translateX(20px)' : 'translateX(0)',
                  backgroundColor: theme.bgPure,
                }}
              >
                {isDark ? <MoonIcon /> : <SunIcon />}
              </div>
            </button>
          </div>

          <div style={styles.settingRow}>
            <div style={styles.settingInfo}>
              <span style={styles.settingLabel}>Skip Title Screen</span>
              <span style={styles.settingDesc}>
                {skipTitleScreen ? 'Go straight to Home' : 'Show Game Boy intro'}
              </span>
            </div>
            <Toggle value={skipTitleScreen} onChange={handleToggleSkipTitle} />
          </div>

          <div style={styles.settingRow}>
            <div style={styles.settingInfo}>
              <span style={styles.settingLabel}>Music</span>
              <span style={styles.settingDesc}>
                {audioSettings.musicEnabled ? 'Background music on' : 'Background music off'}
              </span>
            </div>
            <Toggle value={audioSettings.musicEnabled} onChange={handleToggleMusic} />
          </div>

          <div style={styles.settingRow}>
            <div style={styles.settingInfo}>
              <span style={styles.settingLabel}>Sound Effects</span>
              <span style={styles.settingDesc}>
                {audioSettings.soundEnabled ? 'Button sounds on' : 'Button sounds off'}
              </span>
            </div>
            <Toggle value={audioSettings.soundEnabled} onChange={handleToggleSound} />
          </div>

          <div style={{ ...styles.settingRow, ...styles.settingRowLast }}>
            <div style={styles.settingInfo}>
              <span style={styles.settingLabel}>Digital Cards</span>
              <span style={styles.settingDesc}>
                {preferDigital ? 'Digital cards now ON' : 'Digital cards now OFF'}
              </span>
            </div>
            <Toggle value={preferDigital} onChange={handleToggleImagePreference} />
          </div>
        </div>

        {/* Privacy Section */}
        <h4 style={styles.sectionHeader}>Privacy</h4>
        <div style={styles.section}>
          <div style={{ ...styles.settingRow, ...styles.settingRowLast }}>
            <div style={styles.settingInfo}>
              <span style={styles.settingLabel}>Hide from Search</span>
              <span style={styles.settingDesc}>Hide your profile from "Find Traders" search</span>
            </div>
            <Toggle value={hideProfile} onChange={handleToggleHideProfile} />
          </div>
        </div>

        {/* Feed Section */}
        <h4 style={styles.sectionHeader}>Feed</h4>
        <div style={styles.section}>
          <div style={{ ...styles.settingRow, ...styles.settingRowLast }}>
            <div style={styles.settingInfo}>
              <span style={styles.settingLabel}>Hide My Posts</span>
              <span style={styles.settingDesc}>Hide your own posts from the Home feed</span>
            </div>
            <Toggle value={hideMyPosts} onChange={() => setHideMyPosts(!hideMyPosts)} />
          </div>
        </div>

        {/* Pricing Section - only visible if pricing_trigger enabled on profile */}
        {activeProfile?.pricing_trigger && (
          <>
            <h4 style={styles.sectionHeader}>Pricing</h4>
            <div style={styles.section}>
              <div
                style={{ ...styles.settingRow, ...styles.settingRowLast, cursor: 'pointer' }}
                onClick={() => navigate('/pricing-preferences')}
              >
                <div style={styles.settingInfo}>
                  <span style={styles.settingLabel}>Pricing Preferences</span>
                  <span style={styles.settingDesc}>
                    {pricingEnabled ? 'Prices enabled' : 'Prices hidden'} - tap to configure
                  </span>
                </div>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={theme.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Preferences;
